import React, { useContext, useEffect, useMemo, useState } from 'react';

import { RowSelectionState } from '@tanstack/react-table';
import { DataContext, DataContextActionType } from 'context/DataContext';
import { DateTime } from 'luxon';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import {
  SellerDealsWithCarves_user_company_deals,
  SellerDealsWithCarves_user_company_deals_listing,
} from 'query/__generated__/SellerDealsWithCarves';
import { SELLER_DEALS_WITH_CARVES } from 'query/SellerDealsWithCarves';

import { useActiveTradesContext } from './ActiveTradesContext';
import BidsTable from './BidsTable';
import { FIELD_CONFIG } from '../../../../configs/columns';
import { Button, DownloadDocumentButton } from '../../../../ui-kit';
import { downloadExcelFromJson } from '../../../../utils/excelUtils';
import { basisPointsToPercent } from '../../../deals/DealStages/EventActionCards/utils';
import { adaptBidCardBid, getOwnershipLabel } from './shared/bidUtils';
import { LatestNews } from '../../home/News';
import { formatCentsToDollars, toPercentage } from '../BiddingAndPricing/formatting';

export interface ListingWithBids {
  listing: SellerDealsWithCarves_user_company_deals_listing;
  deals: SellerDealsWithCarves_user_company_deals[];
}

const BidsTab = () => {
  const { dispatch } = useContext(DataContext);
  const { data, loading, error } = useQuery(SELLER_DEALS_WITH_CARVES, {
    fetchPolicy: 'cache-and-network',
  });
  const { selectedListingIds, clearSelectedListingIds } = useActiveTradesContext();
  const navigate = useNavigate();

  const [selectedListings, setSelectedListings] = useState<RowSelectionState>({});

  useEffect(() => {
    dispatch({
      type: DataContextActionType.SET_LOADING,
      payload: loading,
    });
  }, [loading, dispatch]);

  if (error) {
    toast.error(`Failed to load data.`);
  }

  // TODO(ENG-XXXX): Move bid filtering to backend for better performance - currently implemented in frontend for rapid iteration
  const dealsWithBids = (data?.user?.company?.deals ?? []).filter(
    (deal: SellerDealsWithCarves_user_company_deals) => deal.bids && deal.bids.length > 0,
  );

  const listingsWithBids: ListingWithBids[] = useMemo(() => {
    const map = new Map<
      string,
      {
        listing: SellerDealsWithCarves_user_company_deals_listing;
        deals: SellerDealsWithCarves_user_company_deals[];
      }
    >();

    dealsWithBids.forEach((deal: SellerDealsWithCarves_user_company_deals) => {
      const listingId = deal.listing.id;

      if (!map.has(listingId)) {
        map.set(listingId, {
          listing: deal.listing,
          deals: [deal],
        });
      } else {
        map.get(listingId)!.deals.push(deal);
      }
    });

    return Array.from(map.values()) satisfies ListingWithBids[];
  }, [data]);

  const handleDownloadExcel = () => {
    if (!dealsWithBids) {
      toast.error('No data available for download.');
      return;
    }
    const selectedListingsIds = Object.keys(selectedListings).map((id) => id);

    const downloadData: Array<Record<string, any>> = [];
    const filteredData = listingsWithBids.filter((item: ListingWithBids) =>
      selectedListingsIds.length
        ? selectedListingsIds.includes(item.listing.id)
        : item.listing === null,
    );

    filteredData.forEach((data: ListingWithBids) => {
      downloadData.push({
        typename: 'Listing',
        id: data.listing.id,
        providedName: data.listing.providedName,
        createdDate: FIELD_CONFIG.maturity_date.display(data.listing.createdDate),
        assetClass: data.listing.assetClass === 'HOME' ? 'RESI' : data.listing.assetClass,

        upb: formatCentsToDollars(Number(data.listing.upb)),
        loan_count: data.listing.loan_count,
        wa_coupon_bps: toPercentage(Number(data.listing.wa_coupon_bps)),
        wa_loan_age_months: data.listing.wa_loan_age_months,
        wa_remaining_loan_terms_months: data.listing.wa_remaining_loan_terms_months,
        wa_borrower_credit_score: data.listing.wa_borrower_credit_score,
        wa_ltv_bps: toPercentage(Number(data.listing.wa_ltv_bps)),
      });

      data.deals.forEach((deal: SellerDealsWithCarves_user_company_deals) => {
        const latestBid = deal.bids[deal.bids.length - 1];
        console.log('deal', deal);

        const hasDiligenceCompletionDate =
          latestBid.stipulations?.has_diligence_completion_date_seconds;
        const diligenceCompletionDate = latestBid.stipulations?.diligence_completion_date_seconds;
        const hasSettlementDate = latestBid.stipulations?.has_settlement_date_seconds;
        const settlementDate = latestBid.stipulations?.settlement_date_seconds;
        const hasCustomStipulations =
          (latestBid.stipulations?.custom_stipulations?.length ?? 0) > 0;

        const bidBasisPoints = latestBid.stipulations?.bid_basis_points || 0;
        const servicing = Number(latestBid.stipulations?.servicing_rate_bps) || 0;
        const gwac = latestBid.carve_summary?.wa_coupon_bps || 0;
        const nwac = (gwac - servicing) / 100;
        const unpaidBalance = latestBid?.price?.unpaid_balance || 0;
        const participationPercent = Number(
          latestBid.price?.transfer_amount ?? latestBid.stipulations?.transfer_amount ?? 100,
        );

        const netYieldAtPrice = () => {
          const pricePercent = parseFloat(basisPointsToPercent(bidBasisPoints));
          return pricePercent !== 0 ? ((100 * nwac) / pricePercent).toFixed(3) + '%' : '-';
        };

        const purchasePrice = () => {
          const priceDecimal = parseFloat(basisPointsToPercent(bidBasisPoints)) / 100;

          let finalPrice = priceDecimal * unpaidBalance;

          if (participationPercent > 0) {
            finalPrice = priceDecimal * unpaidBalance * (participationPercent / 100);
          }

          return formatCentsToDollars(finalPrice);
        };

        downloadData.push({
          typename: `Bid ${latestBid.bid_id}`,
          id: latestBid.bid_id,
          providedName: 'name' in deal.counterparty ? deal.counterparty.name : '',
          createdDate: FIELD_CONFIG.maturity_date.display(latestBid.created_date),
          assetClass: data.listing.assetClass === 'HOME' ? 'RESI' : data.listing.assetClass,

          upb: '-',
          loan_count: '-',
          wa_coupon_bps: '-',
          wa_loan_age_months: '-',
          wa_remaining_loan_terms_months: '-',
          wa_borrower_credit_score: '-',
          wa_ltv_bps: '-',
          state: deal.state.label,
          ownership: getOwnershipLabel(adaptBidCardBid(latestBid)),
          price: `${basisPointsToPercent(bidBasisPoints)}%`,
          servicing: latestBid.stipulations?.is_servicing_retained
            ? `${((latestBid.stipulations?.servicing_rate_bps ?? 0) / 100).toFixed(2)}%`
            : 'Released',
          netYieldPrice: netYieldAtPrice(),
          ...(latestBid.stipulations?.has_percent_collateral_diligence && {
            percent_collateral_diligence: `${(latestBid.stipulations?.percent_collateral_diligence_bps ?? 0) / 100}%`,
          }),
          ...(latestBid.stipulations?.has_min_net_yield_to_buyer && {
            min_net_yield_to_buyer: `${((latestBid.stipulations?.min_net_yield_to_buyer_bps ?? 0) / 100).toFixed(2)}%`,
          }),
          ...(purchasePrice() && { purchase_dollar_price: purchasePrice() }),
          ...(hasSettlementDate &&
            settlementDate && {
              settlement_date: DateTime.fromSeconds(settlementDate).toLocaleString(
                DateTime.DATE_SHORT,
              ),
            }),
          ...(hasDiligenceCompletionDate &&
            diligenceCompletionDate && {
              settlement_date: DateTime.fromSeconds(diligenceCompletionDate).toLocaleString(
                DateTime.DATE_SHORT,
              ),
            }),
          ...(hasCustomStipulations && {
            custom_stipulations:
              latestBid.stipulations?.custom_stipulations
                ?.filter(Boolean)
                .map((stip, index) => <div key={stip + index}>{stip}</div>) ?? [],
          }),
        });
      });
    });

    downloadExcelFromJson(downloadData, 'Loan Data', 'Loan Data.xlsx');
  };

  const handleCompare = () => {
    const queryString = `?ids=${selectedListingIds.join(',')}`;
    navigate(`/marketplace/active_trades/comparison${queryString}`);
  };

  return (
    <div className="p-4 text-center text-lg text-slate-300">
      <div className="wrap flex justify-between">
        <div className="w-full"></div>
        <div className="flex items-center">
          {selectedListingIds.length > 1 && (
            <>
              <Button
                onClick={handleCompare}
                className="mr-4 h-[35px] min-h-[35px] w-[150px]"
                size="medium"
                type="primary_light"
              >
                Compare
              </Button>
              <Button
                onClick={() => clearSelectedListingIds()}
                className="mr-4 h-[35px] min-h-[35px] w-[150px]"
                type="secondary"
                size="medium"
              >
                Clear Selection
              </Button>
            </>
          )}
          <DownloadDocumentButton
            onClick={handleDownloadExcel}
            fileType="excel"
            tooltipText="Download Excel"
            tooltipPosition="left"
            className="mr-4"
            disabled={Object.keys(selectedListings).length === 0}
          />
        </div>
      </div>
      <BidsTable
        data={listingsWithBids}
        selectedListings={selectedListings}
        setSelectedListings={setSelectedListings}
      />
    </div>
  );
};

export default BidsTab;
